import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { TableLoadTiming } from '../../types/app'

interface MetadataModalProps {
  open: boolean
  onClose: () => void
  data: PlatinumData
  tableName?: string
  timing?: TableLoadTiming
  onReload?: () => void
  isReloading?: boolean
}

const MetadataModal: React.FC<MetadataModalProps> = ({
  open,
  onClose,
  data,
  tableName,
  timing,
  onReload,
  isReloading
}) => {
  const [sigDetailsExpanded, setSigDetailsExpanded] = useState(false)
  const [showRawData, setShowRawData] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  // Helper to access metadata safely
  const getMeta = () => data.meta || (data as any).metadata

  // Helper to access properties
  const getProperties = () => (data as any).properties || (data as any).Properties

  // Check if we have any metadata to show
  const hasMetadata = !!(
    getMeta() ||
    tableName ||
    (data as any).CreatedUtc ||
    (data as any).createdUtc ||
    (data as any).Description ||
    (data as any).description
  )

  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    const jsonString = JSON.stringify(data, null, 2)
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {tableName ? `Table: ${tableName}` : 'Table Information'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showRawData && (
              <IconButton
                onClick={handleCopyToClipboard}
                size="small"
                color={copySuccess ? 'success' : 'primary'}
                title="Copy to clipboard"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: 'grey.500' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: showRawData ? 0 : 2,
        }}
      >
        {showRawData ? (
          // Raw JSON Data View
          <Box>
            <Box
              component="pre"
              sx={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '11px',
                overflow: 'auto',
                backgroundColor: '#f8f9fa',
                padding: 0,
                borderRadius: 0,
                maxHeight: '60vh'
              }}
            >
              {JSON.stringify(data, null, 2)}
            </Box>
            {copySuccess && (
              <Box sx={{ mt: 1, color: 'success.main', fontSize: '12px' }}>
                ✓ Copied to clipboard!
              </Box>
            )}
          </Box>
        ) : (
          // Metadata View
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Technical Information */}
          {((data as any).CreatedUtc || (data as any).createdUtc || (data as any).Description || (data as any).description || timing?.cachedAt || timing?.estimated) && (
            <Box>
              <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                Technical Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
                {/* Show single date field - "Cached at" with reload button if cached, otherwise "Loaded at" */}
                {timing?.cachedAt ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Data cached at:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {new Date(timing.cachedAt).toLocaleString()}
                      </Typography>
                      {onReload && (
                        <IconButton
                          onClick={onReload}
                          disabled={isReloading}
                          size="small"
                          title="Reload table data"
                        >
                          {isReloading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : ((data as any).CreatedUtc || (data as any).createdUtc) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Loaded at:</Typography>
                    <Typography variant="body2">
                      {(() => {
                        const dateStr = (data as any).CreatedUtc || (data as any).createdUtc
                        return dateStr ? new Date(dateStr).toLocaleString() : 'Unknown'
                      })()}
                    </Typography>
                  </Box>
                )}
                {timing?.estimated && timing.estimated > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Typical load time:</Typography>
                    <Typography variant="body2">
                      {(timing.estimated / 1000).toFixed(1)}s
                    </Typography>
                  </Box>
                )}
                {((data as any).Description || (data as any).description) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Description:
                    </Typography>
                    <Typography variant="body2" sx={{
                      display: 'block',
                      pl: 1,
                      fontStyle: 'italic',
                      lineHeight: 1.4,
                      wordBreak: 'break-word'
                    }}>
                      {(data as any).Description || (data as any).description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Table Configuration */}
          {getMeta() && (
            <Box>
              <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                Table Configuration
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 1 }}>
                {(getMeta()?.description) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, pl: 1, fontStyle: 'italic' }}>
                      {getMeta()?.description}
                    </Typography>
                  </Box>
                )}

                {(getMeta()?.top || getMeta()?.Top || getMeta()?.title) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Top Axis (Columns)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, pl: 1, fontStyle: 'italic' }}>
                      {getMeta()?.top || getMeta()?.Top || getMeta()?.title}
                    </Typography>
                  </Box>
                )}

                {(getMeta()?.side || getMeta()?.Side) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Side Axis (Rows)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, pl: 1, fontStyle: 'italic' }}>
                      {getMeta()?.side || getMeta()?.Side}
                    </Typography>
                  </Box>
                )}

                {(getMeta()?.filter || getMeta()?.Filter) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Table Filter
                    </Typography>
                    <Typography variant="body2" sx={{
                      mt: 0.5,
                      pl: 1,
                      fontFamily: 'monospace',
                      backgroundColor: '#f1f3f4',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>
                      {getMeta()?.filter || getMeta()?.Filter}
                    </Typography>
                  </Box>
                )}

                {(getMeta()?.weight || getMeta()?.Weight) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Weighting Variable
                    </Typography>
                    <Typography variant="body2" sx={{
                      mt: 0.5,
                      pl: 1,
                      fontFamily: 'monospace',
                      backgroundColor: '#f1f3f4',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>
                      {getMeta()?.weight || getMeta()?.Weight}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Sample Sizes and Statistical Settings */}
          {getMeta() && (
            (getMeta()?.unweightedBase !== undefined || getMeta()?.UnweightedBase !== undefined ||
             getMeta()?.filteredBase !== undefined || getMeta()?.FilteredBase !== undefined ||
             getMeta()?.sigLevel !== undefined || getMeta()?.SigLevel !== undefined)
          ) && (
            <Box>
              <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                Sample & Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 1 }}>
                {((getMeta()?.unweightedBase !== undefined || getMeta()?.UnweightedBase !== undefined ||
                   getMeta()?.filteredBase !== undefined || getMeta()?.FilteredBase !== undefined)) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                      Sample Sizes
                    </Typography>
                    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(getMeta()?.unweightedBase !== undefined || getMeta()?.UnweightedBase !== undefined) && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Unweighted Base:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(getMeta()?.unweightedBase ?? getMeta()?.UnweightedBase)?.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {(getMeta()?.filteredBase !== undefined || getMeta()?.FilteredBase !== undefined) && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Filtered Base:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(getMeta()?.filteredBase ?? getMeta()?.FilteredBase)?.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {(getMeta()?.sigLevel !== undefined || getMeta()?.SigLevel !== undefined) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Significance Testing
                    </Typography>
                    <Box sx={{ pl: 1, mt: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Confidence Level:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(() => {
                            const sigLevel = getMeta()?.sigLevel || getMeta()?.SigLevel || 0.95
                            const percentage = sigLevel > 1 ? sigLevel : sigLevel * 100
                            return `${percentage.toFixed(1)}%`
                          })()}
                        </Typography>
                      </Box>

                      {/* More Info Button */}
                      {getProperties()?.significance && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setSigDetailsExpanded(!sigDetailsExpanded)}
                            startIcon={sigDetailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              color: 'primary.main',
                              p: 0.5,
                              minWidth: 'auto'
                            }}
                          >
                            More Info
                          </Button>

                          <Collapse in={sigDetailsExpanded}>
                            <Box sx={{
                              mt: 1,
                              p: 1.5,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 1,
                              border: '1px solid #e9ecef'
                            }}>
                              {(() => {
                                const sigProps = getProperties()?.significance || getProperties()?.Significance
                                if (!sigProps) return null

                                const props = sigProps as any

                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                      Detailed Test Parameters
                                    </Typography>

                                    {(props.type || props.Type) && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Test:</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                          {(props.type || props.Type) === 'ColumnGroups' ? 'Column groups' : (props.type || props.Type)}
                                        </Typography>
                                      </Box>
                                    )}

                                    {(props.propStat || props.PropStat) && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Proportions:</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                          {props.propStat || props.PropStat} Test
                                        </Typography>
                                      </Box>
                                    )}

                                    {(props.oneTailed !== undefined || props.OneTailed !== undefined) && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary">Tails:</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                          {(props.oneTailed ?? props.OneTailed) ? 'One' : 'Two'}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )
                              })()}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Performance Information */}
          {timing?.lastRunDuration && timing.lastRunDuration > 0 && (
            <Box>
              <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600, mb: 1 }}>
                Performance
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Last Spec Run Time:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {(timing.lastRunDuration / 1000).toFixed(1)}s
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Empty state */}
          {!hasMetadata && !timing?.estimated && !timing?.lastRunDuration && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No metadata available for this table.
              </Typography>
            </Box>
          )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowRawData(!showRawData)} variant="outlined">
          {showRawData ? 'View Metadata' : 'View Data'}
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default MetadataModal